import type {
  ApiLinkModel,
  AppSettingsContentResponseModel,
  IApiContentResponseModel,
  ImageWithCrops,
} from '@/services/platform/api/model';
import type { SectionCarouselItem } from '@/components/shared/shop/section/section-carousel';
import { LinkMap, LinkRouteUrl, type Link } from '@/lib/core/utils/links';

/**
 * Extracts the first available image URL from a picker item's properties.
 * Supports the common Umbraco property shapes used across the shop content
 * types:
 *  - `imageUrl: string` — Shopify-imported Collection
 *  - `image: ImageWithCrops[]` — CMS-native category/room images
 *  - `media: ImageWithCrops[]` — hero banner style media
 */
function pickImageUrl(properties: Record<string, unknown> | undefined): string | undefined {
  if (!properties) return undefined;
  if (typeof properties.imageUrl === 'string' && properties.imageUrl) {
    return properties.imageUrl;
  }
  const image = properties.image as ImageWithCrops[] | null | undefined;
  if (image && image.length > 0 && image[0]?.url) return image[0].url;
  const media = properties.media as ImageWithCrops[] | null | undefined;
  if (media && media.length > 0 && media[0]?.url) return media[0].url;
  return undefined;
}

function pickLabel(item: IApiContentResponseModel): string {
  const properties = item.properties as Record<string, unknown> | undefined;
  const title = properties?.title;
  if (typeof title === 'string' && title) return title;
  return item.name ?? '';
}

/**
 * Maps an Umbraco picker (array of content responses) to carousel items
 * for the given visual variant. The picker entries are rendered as-is —
 * if a Collection node has expanded children that should appear as
 * individual product cards, the backend will need to expand them server
 * side (Umbraco `expand` parameter) or a separate fetch will be required.
 */
export function mapPickerToCarouselItems(
  picker: IApiContentResponseModel[] | null | undefined,
  variant: 'product' | 'category'
): SectionCarouselItem[] {
  if (!picker) return [];
  return picker.map(item => {
    const properties = item.properties as Record<string, unknown> | undefined;
    const image = pickImageUrl(properties);
    const label = pickLabel(item);

    if (variant === 'product') {
      return {
        id: item.id,
        variant: 'product',
        image,
        name: label,
      };
    }
    return {
      id: item.id,
      variant,
      image,
      label,
    };
  });
}

/**
 * Returns the first link of an ApiLinkModel array, normalised by `LinkMap`.
 */
export function firstLink(link: ApiLinkModel[] | null | undefined): Link | undefined {
  if (!link || link.length === 0) return undefined;
  return LinkMap(link[0]);
}

/*
 * ── Shop page-mapping accessors ───────────────────────────────────────────────
 *
 * All three read one `Settings → Page Mappings` entry and return `undefined` when it is unset. None
 * of them falls back to a literal path, on purpose.
 *
 * A fallback inside an accessor cannot be seen from the call site, so a flow reads as if it were
 * fully mapping-driven while still smuggling a hardcoded URL through. `getShopBagHref` used to do
 * exactly that: it returned `string`, so `returnPath: ... : bagHref` in `ShopCheckoutScreen` looked
 * clean while carrying `/{locale}/shop/shopping-bag` inside it. The literal survives an editor
 * renaming the node and turns a missing mapping into a 404 — the failure lands far from its cause.
 *
 * It also means the three callers each choose what a missing mapping costs them, which they must,
 * because the cost differs: a "view bag" affordance can simply not be offered, while the checkout
 * screen's exits have to go somewhere. Deciding that here would be deciding it for all of them.
 */

/** Returns the shopping-bag page URL, or `undefined` when `shopShoppingBag` is not configured. */
export function getShopBagHref(appSettings: AppSettingsContentResponseModel | undefined): string | undefined {
  return LinkRouteUrl(appSettings?.properties?.shopShoppingBag?.route);
}

/**
 * Returns the shop checkout page URL, or `undefined` when `shopCheckout` is not configured.
 *
 * The checkout page is the anchor the whole flow hangs off — `/delivery` and `/address/new` are
 * client-side segments appended to whatever this resolves to, and `getPageMode` classifies them by
 * comparing against this same mapping. Callers are expected to report the absence rather than guess.
 */
export function getShopCheckoutHref(
  appSettings: AppSettingsContentResponseModel | undefined
): string | undefined {
  return LinkRouteUrl(appSettings?.properties?.shopCheckout?.route);
}

/**
 * Returns the order-confirmed page URL, or `undefined` when `shopOrderConfirmed` is not configured.
 *
 * One caveat for callers: this destination is reached *after* the money has been taken, so refusing
 * to navigate is not an option there — see `ShopCheckoutScreen.goToConfirmation`.
 */
export function getShopOrderConfirmedHref(
  appSettings: AppSettingsContentResponseModel | undefined
): string | undefined {
  return LinkRouteUrl(appSettings?.properties?.shopOrderConfirmed?.route);
}
